import type { FilterChip } from 'src/app/components/rt-filter-chips/rt-filter-chips.component'
import type { CalloutCard, CalloutContext } from 'src/app/pages/schedule/schedule.vm'
import { IsoDate, daysBetween, monthDay, monthDayYear, toIsoDate } from './dates'
import type { BarState } from './editor'
import { NavTarget, links } from './links'
import type { Tone } from './status'

// ---------------------------------------------------------------------------------------------------------------------
// The API payloads (GET /tool/inventory/mine and the rows the writes return). Dates: `*_on` 'yyyy-MM-dd', `*_at` ISO.

export type ToolKind = 'tool' | 'asset'

export type Held =
  | { readonly kind: 'you' }
  | { readonly kind: 'truck'; readonly vehicle_id: number; readonly label: string }
  | { readonly kind: 'checkout'; readonly handoff_id: number | null; readonly from: string; readonly out_on: string | null; readonly due_on: string | null; readonly note: string }

export type Checkout = Extract<Held, { kind: 'checkout' }>

/** The web's kit status, in its priority order; `needs_replacement` names the tools the shop has to swap whatever the status. */
export type KitStatus = 'Return Pending' | 'Return to Confirm' | 'Waiting on Signature' | 'Late' | 'Checked Out' | 'Assigned' | 'Needs Replacement' | 'Available' | 'Broken Up'

export interface KitRef { readonly id: number; readonly number: string; readonly name: string; readonly status: KitStatus; readonly needs_replacement: string | null }

export interface ToolItem {
  readonly id: number
  readonly tag: string | null
  readonly serial: string | null
  readonly name: string
  readonly category: string | null
  readonly kind: ToolKind
  readonly held: Held
  readonly next_cal_due: string | null
  readonly next_bump_due: string | null
  /** The kit this tool belongs to: it checks out, returns and counts with the kit. */
  readonly kit: KitRef | null
}

export interface CountLine {
  readonly key: string
  readonly category: string | null
  readonly name: string
  readonly serials: readonly string[]
  readonly tags: readonly string[]
  readonly expected: number
  readonly counted: number | null
  readonly note: string
  /** A kit is one line counted tool by tool: `tool_ids` are its tools, `found_tool_ids` the ones checked off. */
  readonly kit_id?: number
  readonly kit_number?: string
  readonly names?: readonly string[]
  readonly tool_ids?: readonly number[]
  readonly found_tool_ids?: readonly number[]
}

export interface CountExtra { readonly id: string; readonly name: string; readonly ref: string; readonly note: string; readonly qty: number }

export type CountStatus = 'Assigned' | 'In Progress' | 'Submitted' | 'Approved' | 'Rejected'

export interface ToolCount {
  readonly id: number
  readonly number: string
  readonly technician: { readonly id: number; readonly name: string }
  readonly location: string | null
  readonly trucks: readonly string[]
  readonly assigned_by: string | null
  readonly assigned_on: string | null
  readonly due_on: string | null
  readonly status: CountStatus
  readonly lines: readonly CountLine[]
  readonly extras: readonly CountExtra[]
  readonly rejected: { readonly reason: string; readonly by: string | null; readonly on: string | null } | null
  readonly submitted_at: string | null
  readonly reviewed_by: string | null
  readonly reviewed_at: string | null
  readonly history: readonly { readonly on: string; readonly at: string; readonly by: string; readonly text: string }[]
  readonly updated_at: string
}

export interface HandoffTool { readonly id: number; readonly tag: string | null; readonly serial: string | null; readonly name: string; readonly category: string | null; readonly kit_number?: string | null }

/** Driver: a truck changing drivers; the new driver signs for the truck and the tools on it. */
export type HandoffKind = 'Assignment' | 'Transfer' | 'Checkout' | 'Return' | 'ChargeBack' | 'Driver'
export type HandoffStatus = 'Pending' | 'Accepted' | 'Declined' | 'Cancelled'

export interface HandoffVehicle { readonly id: number; readonly unit: string | null; readonly name: string; readonly items: readonly HandoffTool[] }

export interface Handoff {
  readonly id: number
  readonly number: string
  readonly kind: HandoffKind
  readonly group_key: string | null
  /** null on a Driver Change, which is on a truck. */
  readonly tool: HandoffTool | null
  readonly vehicle?: HandoffVehicle | null
  readonly from: { readonly user_id: number | null; readonly label: string }
  /** On a Return, the person it was handed to (a receiver), or the shop when nobody was named. */
  readonly to: { readonly user_id: number | null; readonly label: string }
  readonly by: string | null
  /** On a Return, the shop or yard the tool goes back to. */
  readonly return_to?: string | null
  readonly note: string
  readonly sent_on: string
  readonly due_on: string | null
  readonly status: HandoffStatus
  readonly decided_on: string | null
  readonly decided_by: string | null
  readonly reply: string | null
  readonly esign: { readonly name: string; readonly at: string } | null
  /** The receiver's signature on a Return they checked in. */
  readonly confirm_esign?: { readonly name: string; readonly at: string } | null
  readonly returns_handoff_id: number | null
  /** One handoff per kit tool, sharing `group_key`; `missing` marks a tool the tech could not find on a kit return. */
  readonly kit?: { readonly id: number; readonly number: string; readonly name: string; readonly missing: boolean } | null
  readonly charge: { readonly amount: number; readonly write_off: number | null; readonly loss_kind: 'Lost' | 'Stolen'; readonly loss_on: string | null; readonly report_number: string | null } | null
}

/** Who a technician can hand a returned tool or kit to: their manager, or someone who checks tools in at the shop. */
export interface Receiver { readonly id: number; readonly name: string; readonly role: 'manager' | 'check_in' }

export type ReportType = 'Broken' | 'Lost' | 'Stolen'
export type ReportStatus = 'Pending' | 'Retired' | 'In Repair' | 'Kept' | 'Lost'

export interface ToolReport {
  readonly id: number
  readonly number: string
  readonly tool: HandoffTool
  readonly technician: { readonly id: number; readonly name: string }
  readonly type: ReportType
  readonly where: string
  readonly what: string
  readonly usable: boolean
  readonly status: ReportStatus
  readonly charge: boolean
  readonly manager_note: string | null
  readonly decided_by: string | null
  readonly decided_on: string | null
  readonly sent_at: string
}

export interface ToolInventory {
  readonly as_of: string
  readonly technician: { readonly id: number; readonly name: string }
  readonly tools: readonly ToolItem[]
  readonly counts: readonly ToolCount[]
  readonly handoffs: readonly Handoff[]
  readonly reports: readonly ToolReport[]
  readonly technicians: readonly { readonly id: number; readonly name: string }[]
  /** Missing on an inventory saved by an older build. */
  readonly receivers?: readonly Receiver[]
}

/** The count editor's unsent work, kept on the device per count until Submit clears it. */
export interface CountDraftLine { readonly key: string; readonly counted: number | null; readonly note: string; readonly found_tool_ids?: readonly number[] }
export interface CountDraft {
  readonly lines: readonly CountDraftLine[]
  readonly extras: readonly CountExtra[]
  readonly saved_at: string
}

// ---------------------------------------------------------------------------------------------------------------------
// Registries keyed by the domain words. Every sentence the screens show is built here, copied from the mock-up.

export type ToolsTab = 'mine' | 'counts' | 'transfers' | 'reports'
export const TOOLS_TABS: readonly { readonly key: ToolsTab; readonly label: string }[] = [
  { key: 'mine', label: 'My Tools' },
  { key: 'counts', label: 'Tool Counts' },
  { key: 'transfers', label: 'Transfers & Sign-Offs' },
  { key: 'reports', label: 'Lost & Broken' },
]
export const isToolsTab = (s: string | null | undefined): s is ToolsTab => TOOLS_TABS.some(t => t.key === s)

export interface StatusLook { readonly label: string; readonly tone: Tone }

export interface CountStatusDef extends StatusLook {
  readonly editable: boolean
  /** The list card's button. */
  readonly button: 'Start' | 'Edit' | 'View'
}

export const COUNT_STATUS = {
  'Assigned': { label: 'Assigned', tone: 'progress', editable: true, button: 'Start' },
  'In Progress': { label: 'In Progress', tone: 'progress', editable: true, button: 'Edit' },
  'Submitted': { label: 'Submitted', tone: 'submitted', editable: false, button: 'View' },
  'Approved': { label: 'Approved', tone: 'approved', editable: false, button: 'View' },
  'Rejected': { label: 'Rejected', tone: 'rejected', editable: true, button: 'Edit' },
} as const satisfies { readonly [K in CountStatus]: CountStatusDef }

const COUNT_STATUS_ORDER = ['Assigned', 'In Progress', 'Submitted', 'Approved', 'Rejected'] as const satisfies readonly CountStatus[]
export const isCountStatus = (s: string | null | undefined): s is CountStatus => (COUNT_STATUS_ORDER as readonly string[]).includes(s ?? '')

/** The kit card's status pill, the web's words. Assigned is a kit left with a person or truck: kits belong at a shop or yard. */
export const KIT_STATUS: { readonly [K in KitStatus]: StatusLook } = {
  'Return Pending': { label: 'Return Pending', tone: 'muted' },
  'Return to Confirm': { label: 'Return to Confirm', tone: 'rejected' },
  'Waiting on Signature': { label: 'Waiting on Signature', tone: 'progress' },
  'Late': { label: 'Late', tone: 'rejected' },
  'Checked Out': { label: 'Checked Out', tone: 'progress' },
  'Assigned': { label: 'Assigned', tone: 'info' },
  'Needs Replacement': { label: 'Needs Replacement', tone: 'rejected' },
  'Available': { label: 'Available', tone: 'approved' },
  'Broken Up': { label: 'Broken Up', tone: 'muted' },
}
const kitStatus = (k: KitRef): StatusLook => KIT_STATUS[k.status] ?? { label: k.status, tone: 'muted' }

const money = (n: number | null | undefined): string => `$${(Number(n) || 0).toFixed(2)}`
const longDate = (raw: string | null | undefined): string => { const d = toIsoDate(raw); return d ? monthDayYear(d) : '' }
const shortDate = (raw: string | null | undefined): string => { const d = toIsoDate(raw); return d ? monthDay(d) : '' }
const shop = (label: string | null | undefined) => label || 'the shop'
const manager = (name: string | null | undefined) => name || 'your manager'
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/** The thing a handoff is about: its tool, or the truck of a Driver Change. */
const subjectName = (h: Handoff) => h.tool?.name ?? h.vehicle?.name ?? ''
const KIT_SUB = 'moves, returns and counts as one'

/**
 * What one signature covers: pending handoffs that sign as one (a group, a kit, a truck, or a return handed to me to
 * check in), a checked-out tool going back, or a checked-out kit going back whole.
 */
export type SignSubject =
  | { readonly kind: 'accept'; readonly handoffs: readonly [Handoff, ...Handoff[]] }
  | { readonly kind: 'return'; readonly tool: ToolItem; readonly checkout: Checkout; readonly number: string }
  | { readonly kind: 'returnKit'; readonly kit: KitRef; readonly tools: readonly [ToolItem, ...ToolItem[]]; readonly checkout: Checkout; readonly number: string }

/** The sign copy's own kinds: a Return signed by the receiver is a check-in, a kit going back is one signature for every tool. */
export type SignKind = HandoffKind | 'CheckIn' | 'ReturnKit'

export interface SignTool { readonly id: number; readonly tag: string | null; readonly serial: string | null; readonly name: string; readonly category: string | null }

/** One shape for the sign copy, whichever subject is being signed. */
export interface SignFacts {
  readonly kind: SignKind
  readonly number: string
  readonly name: string
  readonly tag: string
  readonly serial: string
  readonly from: string
  readonly by: string | null
  readonly sent_on: string | null
  readonly due_on: string | null
  readonly charge: Handoff['charge']
  readonly tools: readonly SignTool[]
  /** A kit request or kit return: every tool is listed, one signature. */
  readonly kit: { readonly number: string; readonly name: string } | null
  /** On a return: the receiver the tech hands it to, once picked. */
  readonly receiver: string | null
  /** On a check-in: the shop the tool goes back to. */
  readonly return_to: string | null
}

const signTool = (t: SignTool | HandoffTool | ToolItem): SignTool => ({ id: t.id, tag: t.tag, serial: t.serial, name: t.name, category: t.category })

export function signFacts(s: SignSubject, receiver: string | null = null): SignFacts {
  if (s.kind === 'return') {
    const t = s.tool
    return { kind: 'Return', number: s.number, name: t.name, tag: t.tag ?? '—', serial: t.serial ?? '', from: s.checkout.from, by: null, sent_on: s.checkout.out_on, due_on: s.checkout.due_on, charge: null, tools: [signTool(t)], kit: null, receiver, return_to: null }
  }
  if (s.kind === 'returnKit') {
    const k = s.kit
    return { kind: 'ReturnKit', number: k.number, name: k.name, tag: k.number, serial: `${plural(s.tools.length, 'tool', 'tools')} · one signature`, from: s.checkout.from, by: null, sent_on: s.checkout.out_on, due_on: s.checkout.due_on, charge: null, tools: s.tools.map(signTool), kit: k, receiver, return_to: null }
  }
  const [h] = s.handoffs
  const kit = h.kit ? { number: h.kit.number, name: h.kit.name } : null
  if (h.kind === 'Driver') {
    const v = h.vehicle
    const tools = (v?.items ?? []).map(signTool)
    return { kind: 'Driver', number: h.number, name: v?.name ?? 'Truck', tag: v?.unit ?? '—', serial: `Truck · ${plural(tools.length, 'tool', 'tools')} on it`, from: h.from.user_id ? h.from.label : '', by: h.by, sent_on: h.sent_on, due_on: null, charge: null, tools, kit: null, receiver: null, return_to: null }
  }
  const tools = s.handoffs.map(x => x.tool).filter((t): t is HandoffTool => !!t).map(signTool)
  const multi = tools.length > 1
  const kind: SignKind = h.kind === 'Return' ? 'CheckIn' : h.kind
  return {
    kind, number: kit ? kit.number : multi ? `${tools.length} tools` : h.number,
    name: kit ? kit.name : multi ? `${tools.length} tools` : h.tool?.name ?? '',
    tag: kit ? kit.number : multi ? tools.map(t => t.tag || t.serial || '').join(', ') : h.tool?.tag ?? '—',
    serial: multi ? `${tools.length} tools · one signature` : h.tool?.serial ?? '',
    from: h.from.label, by: h.by, sent_on: h.sent_on, due_on: h.due_on, charge: h.charge, tools, kit, receiver: null, return_to: h.return_to ?? null,
  }
}

const toolList = (tools: SignFacts['tools']) => tools.map(t => `${t.tag ? `${t.tag} ` : ''}${t.name}`).join('; ')
const possession = (f: SignFacts, one: string, many: string) =>
  f.kit
    ? `I have all ${f.tools.length} tools listed above in my possession. I’m responsible for them${many}`
    : f.tools.length > 1
      ? `I have these ${f.tools.length} tools in my possession: ${toolList(f.tools)}. I’m responsible for them${many}`
      : `I have this tool in my possession. I’m responsible for it${one}`
const KEEP_ONE = ' until it’s returned, transferred, or reported broken.'
const KEEP_MANY = ' until they’re returned, transferred, or reported broken.'
const onInventory = (f: SignFacts) => (f.kit ? `${f.kit.number} is now on your inventory under one signature.` : f.tools.length > 1 ? `All ${f.tools.length} tools are now on your inventory under one signature.` : `${f.name} is now on your inventory.`)
const these = (f: SignFacts) => (f.kit ? `${f.kit.number} ${f.kit.name}` : f.tools.length > 1 ? `all ${f.tools.length} tools listed above` : f.name)

export interface DeclineDialog { readonly title: string; readonly message: string; readonly reason: string; readonly required: boolean; readonly ok: string; readonly done: { readonly title: string; readonly body: string } }

export interface InboxCopy {
  readonly badge: (n: number) => string
  readonly line: (h: Handoff) => string
  readonly accept: (n: number) => string
  readonly declineLabel: string
  readonly decline: (h: Handoff, n: number) => DeclineDialog
}

export interface SignCopy {
  readonly signTitle: (f: SignFacts) => string
  readonly signButton: string
  readonly agreement: (f: SignFacts) => string
  readonly fromLine: (f: SignFacts) => string
  /** The heading over the tool list; the list only shows for several tools, a kit or a truck. */
  readonly listTitle: (f: SignFacts) => string
  readonly done: (f: SignFacts) => { readonly title: string; readonly body: string }
}

export interface HandoffKindDef extends SignCopy {
  /** Card copy for a Pending handoff addressed to me (a Return addressed to me is a check-in to confirm). */
  readonly inbox: InboxCopy | null
}

const declineTool = (h: Handoff, n: number): DeclineDialog =>
  n > 1
    ? { title: `Decline ${n} Tools`, message: 'Why are you declining? This goes to your manager.', reason: 'I do not have these tools', required: false, ok: 'Decline →', done: { title: 'Declined', body: 'Your manager was told. The tools did not move.' } }
    : { title: 'Decline Tool', message: 'Why are you declining? This goes to your manager.', reason: 'I do not have this tool', required: false, ok: 'Decline →', done: { title: 'Declined', body: 'Your manager was told. The tool did not move.' } }

const signForTools = (n: number) => (n > 1 ? `Sign for ${n} tools` : 'Sign for this tool')
const haveIt = (n: number) => (n > 1 ? `I Have All ${n} · Sign →` : 'I Have It · Sign →')
const multiTitle = (f: SignFacts, single: string) => (f.kit ? 'Sign for Kit' : f.tools.length > 1 ? `Sign for ${f.tools.length} Tools` : single)
const checkAll = (f: SignFacts) => (f.kit || f.tools.length > 1 ? `Check you have all ${f.tools.length} tools before you sign` : '')

export const HANDOFF_KIND: { readonly [K in HandoffKind]: HandoffKindDef } = {
  Assignment: {
    inbox: {
      badge: signForTools,
      line: h => `${h.by || 'Your manager'} assigned this to you · ${longDate(h.sent_on)}`,
      accept: haveIt, declineLabel: 'Decline', decline: declineTool,
    },
    signTitle: f => multiTitle(f, 'Sign for Tool'),
    signButton: 'Sign & Accept →',
    agreement: f => possession(f, KEEP_ONE, KEEP_MANY),
    fromLine: f => `Assigned by ${manager(f.by)} · ${longDate(f.sent_on)}`,
    listTitle: checkAll,
    done: f => ({ title: f.tools.length > 1 ? 'Tools Signed For' : 'Tool Signed For', body: onInventory(f) }),
  },
  Transfer: {
    inbox: {
      badge: n => (n > 1 ? signForTools(n) : 'Transfer to you'),
      line: h => `${h.from.label} is handing this to you · ${longDate(h.sent_on)}`,
      accept: haveIt, declineLabel: 'Decline',
      decline: (h, n) => n > 1 ? declineTool(h, n) : {
        title: 'Decline Transfer', message: `Why are you declining? This goes to ${h.from.label} and your manager.`, reason: 'Did not receive it', required: false, ok: 'Decline →',
        done: { title: 'Declined', body: `${h.from.label} and your manager were told. The tool did not move.` },
      },
    },
    signTitle: f => multiTitle(f, 'Accept Transfer'),
    signButton: 'Sign & Accept →',
    agreement: f => possession(f, KEEP_ONE, KEEP_MANY),
    fromLine: f => `From ${f.from} · ${longDate(f.sent_on)}`,
    listTitle: checkAll,
    done: f => ({ title: f.tools.length > 1 ? 'Tools Signed For' : 'Transfer Accepted', body: onInventory(f) }),
  },
  Checkout: {
    inbox: {
      badge: n => (n > 1 ? `Temporary check-out · ${n} tools` : 'Temporary check-out'),
      line: h => `From ${shop(h.from.label)} · due back ${longDate(h.due_on)}`,
      accept: haveIt, declineLabel: 'Decline', decline: declineTool,
    },
    signTitle: f => (f.kit ? 'Sign for Kit Check-Out' : f.tools.length > 1 ? `Sign for ${f.tools.length}-Tool Check-Out` : 'Sign for Check-Out'),
    signButton: 'Sign & Accept →',
    agreement: f => possession(f, ` and will return it to ${shop(f.from)} by ${longDate(f.due_on)}.`, ` and will return them to ${shop(f.from)} by ${longDate(f.due_on)}.`),
    fromLine: f => `Temporary check-out from ${shop(f.from)} · due back ${longDate(f.due_on)} · ${longDate(f.sent_on)}`,
    listTitle: checkAll,
    done: f => ({
      title: 'Check-Out Signed',
      body: f.kit
        ? `${f.kit.number} is now checked out to you. Return it whole to ${shop(f.from)} by ${longDate(f.due_on)}.`
        : f.tools.length > 1
          ? `All ${f.tools.length} tools are now checked out to you. Return them to ${shop(f.from)} by ${longDate(f.due_on)}.`
          : `${f.name} is now checked out to you. Return it to ${shop(f.from)} by ${longDate(f.due_on)}.`,
    }),
  },
  // A Return addressed to me is one I check in for the shop; the Return I sign myself is SIGN_COPY.Return.
  Return: {
    inbox: {
      badge: n => (n > 1 ? 'Kit check-in to confirm' : 'Check-in to confirm'),
      line: h => `${h.from.label} is returning this to ${shop(h.return_to)} · was due ${longDate(h.due_on)}`,
      accept: () => 'Received · Sign →', declineLabel: 'Not Received',
      decline: h => ({
        title: 'Not Received', message: `Tell ${h.from.label} why. It stays checked out to them.`, reason: '', required: true, ok: 'Send →',
        done: { title: 'Not Received', body: `${h.from.label} was told. It stays checked out to them.` },
      }),
    },
    signTitle: f => (f.kit ? 'Check In Kit' : f.tools.length > 1 ? `Check In ${f.tools.length} Tools` : 'Check In Tool'),
    signButton: 'Sign & Check In →',
    agreement: f => `I received ${these(f)} from ${f.from} and checked ${f.kit || f.tools.length > 1 ? 'them' : 'it'} in to ${shop(f.return_to)} today.`,
    fromLine: f => `From ${f.from} · back to ${shop(f.return_to)} · was due ${longDate(f.due_on)}`,
    listTitle: f => (f.kit ? `Check every tool in ${f.kit.number} is here before you sign` : f.tools.length > 1 ? `Check all ${f.tools.length} tools are here before you sign` : ''),
    done: f => ({ title: 'Checked In', body: `${f.kit ? f.kit.number : f.tools.length > 1 ? `All ${f.tools.length} tools are` : `${f.name} is`} back at ${shop(f.return_to)}. ${f.from} sees it on their app.` }),
  },
  Driver: {
    inbox: {
      badge: () => 'Truck assigned to you',
      line: h => `${manager(h.by)} is making you the driver of ${subjectName(h)}${h.from.user_id ? ` · replaces ${h.from.label}` : ''} · ${longDate(h.sent_on)}`,
      accept: () => 'Accept Truck · Sign →', declineLabel: 'Decline',
      decline: () => ({
        title: 'Decline Truck', message: 'Why are you declining? This goes to your manager.', reason: 'I am not driving this truck', required: false, ok: 'Decline →',
        done: { title: 'Declined', body: 'Your manager was told. The truck did not change drivers.' },
      }),
    },
    signTitle: () => 'Accept Truck',
    signButton: 'Sign & Accept Truck →',
    agreement: f => `I’m taking over as driver of ${f.name}. I’m responsible for the truck${f.tools.length ? ` and the ${plural(f.tools.length, 'tool', 'tools')} listed above` : ''}.`,
    fromLine: f => `${f.from ? `From ${f.from} · ` : ''}assigned by ${manager(f.by)} · ${longDate(f.sent_on)}`,
    listTitle: f => `Tools on this truck (${f.tools.length}) · check they’re all there before you sign`,
    done: f => ({ title: 'Truck Accepted', body: `You’re now the driver of ${f.name}. The tools on it are on your inventory.` }),
  },
  ChargeBack: {
    inbox: {
      badge: () => 'Charge-back to sign',
      line: h => `${h.charge?.loss_kind ?? 'Lost'} tool · ${money(h.charge?.amount)} · from ${manager(h.by)}`,
      accept: () => 'Review & Sign →', declineLabel: 'Dispute',
      decline: () => ({
        title: 'Dispute Charge-Back', message: 'Why are you disputing this charge-back? Your manager will follow up.', reason: '', required: true, ok: 'Send Dispute →',
        done: { title: 'Dispute Sent', body: 'Your manager will follow up on this charge-back.' },
      }),
    },
    signTitle: () => 'Authorize Deduction',
    signButton: 'Sign & Authorize →',
    agreement: f => `I authorize Roto-Versal Energy Services to deduct ${money(f.charge?.amount)} from my next paycheck for the ${(f.charge?.loss_kind ?? 'Lost').toLowerCase()} ${f.name}${f.tag && f.tag !== '—' ? ` (${f.tag})` : ''} that was assigned to me.`,
    fromLine: f => `${f.charge?.loss_kind ?? 'Lost'} · ${money(f.charge?.amount)} charge-back from ${manager(f.by)} · ${longDate(f.sent_on)}`,
    listTitle: () => '',
    done: () => ({ title: 'Charge-Back Signed', body: 'Your signature was sent to payroll. A copy is kept with your tool file.' }),
  },
}

const handedTo = (f: SignFacts) => (f.receiver ? `${f.receiver}, to check in to ${shop(f.from)}` : shop(f.from))

/** The sign copy per sign kind: the handoff kinds, plus the Return the tech signs and the kit return. */
export const SIGN_COPY: { readonly [K in SignKind]: SignCopy } = {
  ...HANDOFF_KIND,
  CheckIn: HANDOFF_KIND.Return,
  Return: {
    signTitle: () => 'Return Tool',
    signButton: 'Sign & Return →',
    agreement: f => `I returned this tool to ${handedTo(f)} today. It stays on my inventory until they confirm they received it.`,
    fromLine: f => `Returning to ${shop(f.from)} · was due ${longDate(f.due_on)}`,
    listTitle: () => '',
    done: f => ({ title: 'Return Signed', body: `You signed ${f.name} back to ${handedTo(f)}. It stays on your inventory until they confirm they received it.` }),
  },
  ReturnKit: {
    signTitle: () => 'Return Kit',
    signButton: 'Sign & Return →',
    agreement: f => `I returned ${f.number} to ${handedTo(f)} today as one kit. Every tool I checked is in it, and any missing tool is noted. The kit stays on my inventory until the shop confirms.`,
    fromLine: f => `Returning to ${shop(f.from)} · was due ${longDate(f.due_on)}`,
    listTitle: () => 'Count every tool · the whole kit goes back together',
    done: f => ({ title: 'Kit Return Signed', body: `You signed ${f.number} back to ${handedTo(f)}. It stays on your inventory until they confirm they received it.` }),
  },
}

export interface ReportStatusDef extends StatusLook { readonly line: (r: ToolReport) => string }

export const REPORT_STATUS: { readonly [K in ReportStatus]: ReportStatusDef } = {
  Pending: { label: 'Pending review', tone: 'progress', line: () => 'Waiting on your manager' },
  Retired: { label: 'Retired', tone: 'muted', line: r => `Retired by ${manager(r.decided_by)} · removed from your inventory` },
  'In Repair': { label: 'Sent to repair', tone: 'progress', line: r => `Sent out for repair by ${manager(r.decided_by)}` },
  Kept: { label: 'Keep using it', tone: 'approved', line: r => (r.type === 'Broken' ? `Reviewed by ${manager(r.decided_by)} · stays on your inventory` : 'Still looking · stays on your inventory') },
  Lost: { label: 'Written off', tone: 'dnr', line: r => `Written off by ${manager(r.decided_by)} · removed from your inventory${r.charge ? ' · charge-back sent to sign' : ''}` },
}

export interface ReportTypeDef { readonly whereLabel: string; readonly whatPlaceholder: string; readonly foot: string }

const LOSS_FOOT = 'Your manager reviews this in RotoTrack. A lost or stolen tool comes off your inventory and may be charged back to you.'

export const REPORT_TYPE: { readonly [K in ReportType]: ReportTypeDef } = {
  Broken: { whereLabel: 'Where did it break?', whatPlaceholder: 'e.g. Dropped it off the tailgate. Display cracked and it won’t zero.', foot: 'Your manager reviews this in RotoTrack. If they retire the tool, it comes off your inventory and future counts.' },
  Lost: { whereLabel: 'Where was it last seen?', whatPlaceholder: 'e.g. Had it on the tailgate at the last stop, not in the truck when I got back.', foot: LOSS_FOOT },
  Stolen: { whereLabel: 'Where was it stolen?', whatPlaceholder: 'e.g. Truck was broken into overnight at the yard. Police report #…', foot: LOSS_FOOT },
}
const REPORT_TYPES = ['Broken', 'Lost', 'Stolen'] as const satisfies readonly ReportType[]
const WHERE_PLACEHOLDER = 'Lease, location or work order · e.g. Cotton Draw 14-1 Bty, PRV-88949'

export const OFFLINE_TOAST = 'You’re offline. Connect and try again.'

/** "Last sync 3:41 PM" today, "Last sync 10/23 3:41 PM" on another day, '' before the first sync. */
export function syncLabel(lastUpdate: string | null | undefined, today: IsoDate): string {
  const day = toIsoDate(lastUpdate)
  const time = lastUpdate?.split(', ')[1]
  if (!day || !time) return ''
  return `Last sync ${day === today ? time : `${monthDay(day)} ${time}`}`
}

// ---------------------------------------------------------------------------------------------------------------------
// Shared lookups

const byIdDesc = <T extends { readonly id: number }>(a: T, b: T) => b.id - a.id
const idLine = (t: { readonly tag: string | null; readonly serial: string | null }) => `${t.tag || '—'} · S/N ${t.serial ?? ''}`

/** The latest Return the tech signed for a checked-out tool, if any: Pending means the receiver has not confirmed it yet. */
function returnOf(inv: ToolInventory, tool: ToolItem): Handoff | null {
  if (tool.held.kind !== 'checkout') return null
  const id = tool.held.handoff_id
  return inv.handoffs
    .filter(h => h.kind === 'Return' && h.tool?.id === tool.id && h.status !== 'Cancelled' && (id == null || h.returns_handoff_id === id))
    .sort(byIdDesc)[0] ?? null
}

const isPending = (h: Handoff) => h.status === 'Pending'
const toMe = (inv: ToolInventory) => (h: Handoff) => h.to.user_id === inv.technician.id
const fromMe = (inv: ToolInventory) => (h: Handoff) => h.from.user_id === inv.technician.id
/** Who confirms a return: the receiver it was handed to, else the shop. */
const confirmer = (ret: Handoff) => (ret.to.user_id ? ret.to.label : shop(ret.return_to ?? ret.to.label))

/** Pending handoffs waiting on my signature, charge-backs aside: tools and trucks to sign for, returns handed to me to check in. */
const inbox = (inv: ToolInventory) => inv.handoffs.filter(h => isPending(h) && toMe(inv)(h) && h.kind !== 'ChargeBack').sort(byIdDesc)

/** The kits on my roster, each with its tools (which all sit with the same holder, so the first tool speaks for the kit). */
function kitsOf(inv: ToolInventory): readonly { readonly kit: KitRef; readonly tools: readonly [ToolItem, ...ToolItem[]] }[] {
  const kits = new Map<number, { kit: KitRef; tools: [ToolItem, ...ToolItem[]] }>()
  for (const t of inv.tools) {
    if (!t.kit) continue
    const k = kits.get(t.kit.id)
    if (k) k.tools.push(t)
    else kits.set(t.kit.id, { kit: t.kit, tools: [t] })
  }
  return Array.from(kits.values(), k => ({ ...k, tools: k.tools.slice().sort((a, b) => (a.tag ?? '').localeCompare(b.tag ?? '')) as [ToolItem, ...ToolItem[]] }))
}

/** A tech's pending Lost / Broken report on a tool, which marks its kit Needs Replacement until the shop decides. */
const pendingReportOn = (inv: ToolInventory, toolId: number) => inv.reports.find(r => r.status === 'Pending' && r.tool.id === toolId) ?? null
const pendingChargeBacks = (inv: ToolInventory) => inv.handoffs.filter(h => isPending(h) && toMe(inv)(h) && h.kind === 'ChargeBack').sort(byIdDesc)
const openCounts = (inv: ToolInventory) => inv.counts.filter(c => COUNT_STATUS[c.status].editable)

/** "Assigned to you" | "On TRK-1212" | "Checked out to you · due back Oct 24" */
function heldLabel(t: ToolItem): string {
  switch (t.held.kind) {
    case 'you': return 'Assigned to you'
    case 'truck': return `On ${t.held.label}`
    case 'checkout': return `Checked out to you${t.held.due_on ? ` · due back ${shortDate(t.held.due_on)}` : ''}`
  }
}

/** The soonest calibration or bump test on a tool, in days from today. */
function nextService(t: ToolItem, today: IsoDate): { readonly what: 'Calibration' | 'Bump test'; readonly on: IsoDate; readonly days: number } | null {
  const cal = toIsoDate(t.next_cal_due)
  const bump = toIsoDate(t.next_bump_due)
  const pick: ['Calibration' | 'Bump test', IsoDate] | null = bump && (!cal || bump < cal) ? ['Bump test', bump] : cal ? ['Calibration', cal] : null
  return pick ? { what: pick[0], on: pick[1], days: daysBetween(today, pick[1]) } : null
}

const SERVICE_WINDOW_DAYS = 30

// ---------------------------------------------------------------------------------------------------------------------
// My Tools

export interface ToolFlag { readonly text: string; readonly tone: Tone }

export interface ToolRowVM {
  readonly id: number
  readonly name: string
  readonly idLine: string
  readonly flag: ToolFlag | null
  readonly canReturn: boolean
  readonly canTransfer: boolean
  readonly canReport: boolean
}

/** A kit is one card: its status pill, the tools the shop has to swap, and a bordered list of its tools with their own flags. */
export interface KitRowVM extends ToolRowVM {
  readonly kit: {
    readonly id: number
    readonly number: string
    readonly status: StatusLook
    readonly note: string | null
    readonly tools: readonly ToolRowVM[]
  }
}

export type MyToolsRow = ToolRowVM | KitRowVM
export const isKitRow = (r: MyToolsRow): r is KitRowVM => 'kit' in r

export interface ToolGroupVM { readonly key: string; readonly title: string; readonly tone: Tone; readonly rows: readonly MyToolsRow[] }

export interface MyToolsVM {
  readonly total: number
  readonly sub: string
  readonly groups: readonly ToolGroupVM[]
  readonly empty: string | null
}

const toolIdLine = (t: ToolItem) => `${t.tag || 'No ID'} · S/N ${t.serial ?? ''} · ${t.category ?? ''}`

function checkoutFlag(inv: ToolInventory, t: ToolItem, today: IsoDate): { readonly flag: ToolFlag; readonly waiting: boolean } {
  const ret = returnOf(inv, t)
  const waiting = ret?.status === 'Pending'
  const co = t.held as Checkout
  const due = toIsoDate(co.due_on)
  const overdue = !!due && daysBetween(today, due) < 0
  const flag = waiting && ret
    ? { text: `Returned · waiting on ${confirmer(ret)} to confirm`, tone: 'muted' as const }
    : { text: `Due back ${due ? monthDayYear(due) : ''}${overdue ? ' · overdue' : ''}`, tone: overdue ? 'rejected' as const : 'progress' as const }
  return { flag, waiting }
}

/** A tool's own flag: inside a kit, a report waiting on the shop; else the next calibration or bump test inside 30 days. */
function serviceFlag(inv: ToolInventory, t: ToolItem, today: IsoDate): ToolFlag | null {
  const report = t.kit ? pendingReportOn(inv, t.id) : null
  if (report) return { text: `Reported ${report.type.toLowerCase()} · ${report.number} · waiting on the shop`, tone: 'rejected' }
  const next = nextService(t, today)
  return !next || next.days > SERVICE_WINDOW_DAYS ? null
    : next.days < 0 ? { text: `${next.what} overdue · take it to the shop`, tone: 'rejected' }
    : { text: `${next.what} due ${monthDayYear(next.on)}`, tone: 'progress' }
}

function toolRow(inv: ToolInventory, t: ToolItem, today: IsoDate): ToolRowVM {
  const base = { id: t.id, name: t.name, idLine: toolIdLine(t), canReport: true }
  if (t.held.kind === 'checkout') {
    const { flag, waiting } = checkoutFlag(inv, t, today)
    return { ...base, flag, canReturn: !waiting, canTransfer: false }
  }
  return { ...base, flag: serviceFlag(inv, t, today), canReturn: false, canTransfer: !t.kit }
}

function kitRow(inv: ToolInventory, kit: KitRef, tools: readonly [ToolItem, ...ToolItem[]], today: IsoDate): KitRowVM {
  const [first] = tools
  const out = first.held.kind === 'checkout'
  const { flag, waiting } = out ? checkoutFlag(inv, first, today) : { flag: null, waiting: false }
  return {
    id: first.id, name: kit.name, idLine: `${kit.number} · ${plural(tools.length, 'tool', 'tools')} · ${KIT_SUB}`, flag,
    canReturn: out && !waiting, canTransfer: false, canReport: false,
    kit: {
      id: kit.id, number: kit.number, status: kitStatus(kit),
      note: kit.needs_replacement ? `${kit.needs_replacement} · the shop has to swap it before the kit can go out` : null,
      tools: tools.map(t => ({ id: t.id, name: t.name, idLine: toolIdLine(t), flag: serviceFlag(inv, t, today), canReturn: false, canTransfer: false, canReport: true })),
    },
  }
}

const matchesTool = (t: ToolItem, q: string) => [t.name, t.tag, t.serial, t.category, t.kit?.number, t.kit?.name].join(' ').toLowerCase().includes(q)

export function myToolsVM(inv: ToolInventory, query: string, today: IsoDate): MyToolsVM {
  const q = query.trim().toLowerCase()
  const shown = inv.tools.filter(t => !q || matchesTool(t, q))
  const kits = kitsOf(inv).filter(k => k.tools.some(t => shown.includes(t)))
  // Loose tools first, then kits, each where its holder puts it.
  const rows = (pick: (t: ToolItem) => boolean): MyToolsRow[] => [
    ...shown.filter(t => !t.kit && pick(t)).map(t => toolRow(inv, t, today)),
    ...kits.filter(k => pick(k.tools[0])).map(k => kitRow(inv, k.kit, k.tools, today)),
  ]
  const trucks = new Map<number, string>()
  for (const t of inv.tools) if (t.held.kind === 'truck') trucks.set(t.held.vehicle_id, t.held.label)
  const groups: ToolGroupVM[] = [
    { key: 'co', title: 'Checked out to you', tone: 'progress' as const, rows: rows(t => t.held.kind === 'checkout') },
    { key: 'you', title: 'Assigned to you', tone: 'rejected' as const, rows: rows(t => t.held.kind === 'you') },
    ...Array.from(trucks, ([id, label]) => ({ key: `truck-${id}`, title: `On truck ${label}`, tone: 'dnr' as const, rows: rows(t => t.held.kind === 'truck' && t.held.vehicle_id === id) })),
  ].filter(g => g.rows.length > 0)
  const allKits = kitsOf(inv)
  const n = (kind: Held['kind']) => inv.tools.filter(t => !t.kit && t.held.kind === kind).length
  const k = (kind: Held['kind']) => allKits.filter(x => x.tools[0].held.kind === kind).length
  const part = (kind: Held['kind'], label: string) => [
    ...(n(kind) ? [`${plural(n(kind), 'tool', 'tools')} ${label}`] : []),
    ...(k(kind) ? [`${plural(k(kind), 'kit', 'kits')} ${label}`] : []),
  ]
  return {
    total: inv.tools.length,
    sub: [...part('you', 'on you'), ...part('truck', 'on your truck'), ...part('checkout', 'checked out')].join(' · ') || '0 on you',
    groups,
    empty: shown.length ? null : q ? 'No tools match that search.' : 'No tools are assigned to you right now.',
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// Tool Counts

export type CountFilter = 'All' | CountStatus

export interface CountCardVM {
  readonly key: number
  readonly number: string
  readonly status: StatusLook
  readonly readOnly: boolean
  readonly title: string
  readonly sub: string
  readonly meta: readonly { readonly label: 'Due' | 'Submitted' | 'Reviewed by'; readonly value: string }[]
  readonly reason: string | null
  readonly by: string
  readonly button: CountStatusDef['button']
  readonly target: NavTarget
}

export interface CountsVM {
  readonly total: number
  readonly chips: readonly FilterChip[]
  readonly open: { readonly title: string; readonly cards: readonly CountCardVM[] } | null
  readonly past: readonly CountCardVM[]
  readonly empty: { readonly title: string; readonly sub: string } | null
}

const countedLines = (c: Pick<ToolCount, 'lines'>) => c.lines.filter(l => l.counted != null).length
const expectedTotal = (c: Pick<ToolCount, 'lines'>) => c.lines.reduce((t, l) => t + l.expected, 0)

function countSummary(c: ToolCount): string {
  const total = c.lines.reduce((t, l) => t + (l.counted ?? l.expected), 0)
  const off = c.lines.filter(l => l.counted != null && l.counted !== l.expected).length
  return `${plural(total, 'tool', 'tools')}${off ? ` · ${plural(off, 'line', 'lines')} off` : ' · All match'}`
}

function countCard(c: ToolCount): CountCardVM {
  const def = COUNT_STATUS[c.status]
  const reviewer = c.status === 'Approved' || c.status === 'Rejected' ? c.reviewed_by || c.rejected?.by || null : null
  const sub = c.status === 'Assigned' ? 'Not started'
    : c.status === 'Rejected' ? 'Sent back'
    : c.status === 'In Progress' ? `Draft saved · ${countedLines(c)} of ${c.lines.length} counted`
    : countSummary(c)
  const meta = [
    { label: 'Due' as const, value: shortDate(c.due_on) },
    { label: 'Submitted' as const, value: shortDate(c.submitted_at) },
    { label: 'Reviewed by' as const, value: reviewer ?? '' },
  ].filter(m => m.value)
  return {
    key: c.id, number: c.number, status: def, readOnly: !def.editable,
    title: `${c.location ? `Count ${c.location}` : 'Tool Count'} · ${plural(expectedTotal(c), 'tool', 'tools')}`,
    sub, meta, reason: c.status === 'Rejected' ? c.rejected?.reason ?? null : null, by: c.technician.name, button: def.button,
    target: links.toolCount(c.id),
  }
}

export function countsVM(inv: ToolInventory, filter: CountFilter, query: string): CountsVM {
  const q = query.trim().toLowerCase()
  const all = inv.counts
  const chips: FilterChip[] = [
    { key: 'All', label: 'All', count: all.length, tone: null },
    ...COUNT_STATUS_ORDER.map(s => ({ key: s, label: COUNT_STATUS[s].label, count: all.filter(c => c.status === s).length, tone: COUNT_STATUS[s].tone })),
  ]
  const shown = all.filter(c => (filter === 'All' || c.status === filter) && (!q || c.number.toLowerCase().includes(q))).map(countCard)
  const open = shown.filter(c => !c.readOnly)
  return {
    total: all.length,
    chips,
    open: open.length ? { title: open.length === 1 ? 'Open Count' : `${open.length} Open Counts`, cards: open } : null,
    past: shown.filter(c => c.readOnly),
    empty: shown.length ? null
      : filter === 'All' ? { title: 'No tool counts yet', sub: 'When your manager assigns a count, it shows up here with a due date.' }
      : { title: `No ${filter.toLowerCase()} counts`, sub: 'Tap All to see every count.' },
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// Count editor

export interface KitCountTool { readonly id: number; readonly name: string; readonly idLine: string; readonly on: boolean }

export interface CountRowVM {
  readonly key: string
  readonly category: string
  readonly name: string
  readonly expected: number
  readonly counted: number | null
  readonly status: StatusLook
  readonly matched: boolean
  readonly note: string
  /** A kit line: one checkbox per tool instead of a stepper. */
  readonly kit: { readonly number: string; readonly tools: readonly KitCountTool[]; readonly found: readonly number[] } | null
  /** A short kit needs a note saying which tool is missing and where it is. */
  readonly needsNote: boolean
}

export interface CountEditorVM {
  readonly id: number
  readonly number: string
  readonly status: StatusLook
  readonly locked: boolean
  readonly context: readonly { readonly label: string; readonly value: string }[]
  readonly hint: string
  readonly rejected: { readonly head: string; readonly reason: string } | null
  readonly lock: string | null
  readonly countLabel: 'Your Count' | 'Final Count'
  readonly rows: readonly CountRowVM[]
  readonly extras: readonly CountExtra[]
  readonly bar: BarState
  readonly ready: boolean
  readonly assignedBy: string
}

/** The draft laid over the server copy; a draft is only honoured while the count is still the tech's to edit. */
function mergedLines(c: ToolCount, draft: CountDraft | null): readonly CountLine[] {
  if (!draft || !COUNT_STATUS[c.status].editable) return c.lines
  const by = new Map(draft.lines.map(l => [l.key, l]))
  return c.lines.map(l => { const d = by.get(l.key); return d ? { ...l, counted: d.counted, note: d.note, ...(d.found_tool_ids ? { found_tool_ids: d.found_tool_ids } : {}) } : l })
}

function rowStatus(expected: number, counted: number | null): StatusLook {
  if (counted == null) return { label: 'Not counted', tone: 'muted' }
  const d = counted - expected
  return d === 0 ? { label: 'Matches', tone: 'approved' } : d < 0 ? { label: `${-d} short`, tone: 'rejected' } : { label: `${d} over`, tone: 'progress' }
}

/** The kit tools checked off: `found_tool_ids` when the line has them, else the first `counted` tools of a count made before the checklist. */
export function kitFound(l: CountLine): readonly number[] | null {
  if (!l.kit_id) return null
  if (l.found_tool_ids) return l.found_tool_ids
  return l.counted == null ? [] : (l.tool_ids ?? []).slice(0, l.counted)
}

function countRow(l: CountLine): CountRowVM {
  const found = kitFound(l)
  const kit = l.kit_id && found
    ? {
        number: l.kit_number ?? '', found,
        tools: (l.tool_ids ?? []).map((id, i) => ({ id, name: l.names?.[i] ?? '', idLine: `${l.tags[i] ?? ''}${l.serials[i] ? ` · S/N ${l.serials[i]}` : ''}`, on: found.includes(id) })),
      }
    : null
  const short = l.counted != null && l.counted < l.expected
  return {
    key: l.key, category: kit ? 'Tool Kit · check off every tool' : l.category ?? '', name: l.name, expected: l.expected, counted: l.counted,
    status: rowStatus(l.expected, l.counted), matched: l.counted === l.expected, note: l.note, kit,
    needsNote: !!kit && short && !l.note.trim(),
  }
}

export const KIT_SHORT_HINT = 'Unchecked tools are missing from the kit. Add a note saying which one and where it is.'

export function countEditorVM(inv: ToolInventory, id: number, draft: CountDraft | null): CountEditorVM | null {
  const c = inv.counts.find(x => x.id === id)
  if (!c) return null
  const def = COUNT_STATUS[c.status]
  const locked = !def.editable
  const lines = mergedLines(c, draft)
  const extras = draft && !locked ? draft.extras : c.extras
  const rows = lines.map(countRow)
  const done = lines.filter(l => l.counted != null).length
  const needNote = rows.filter(r => r.needsNote).length
  const ready = done === lines.length && needNote === 0
  const reviewer = c.reviewed_by || c.rejected?.by || null
  const latestApproved = inv.counts.filter(x => x.status === 'Approved').sort(byIdDesc)[0]
  const lock = c.status === 'Submitted' ? 'Submitted. Locked while your manager reviews it.'
    : c.status === 'Approved'
      ? latestApproved?.id === c.id
        ? `This is your current inventory. Approved ${shortDate(c.reviewed_at)} by ${manager(reviewer)}.`
        : `Approved ${shortDate(c.reviewed_at)} by ${manager(reviewer)}. A newer count is your current inventory.`
      : null
  return {
    id: c.id, number: c.number, status: def, locked,
    context: [
      c.location ? { label: 'Counted By', value: c.technician.name } : { label: 'Technician', value: c.technician.name },
      c.location ? { label: 'Location', value: c.location } : { label: 'Truck', value: c.trucks[0] ?? 'No truck' },
      { label: 'Assigned By', value: manager(c.assigned_by) },
      locked ? { label: 'Counted', value: longDate(c.submitted_at) } : { label: 'Due', value: longDate(c.due_on) },
    ],
    hint: `${c.location ? `Count every tool at ${c.location}.` : 'Count every tool on you and in your truck.'} Enter one total per tool type${rows.some(r => r.kit) ? ', and check off each tool in a kit' : ''}.`,
    rejected: c.status === 'Rejected' && c.rejected ? { head: `Rejected by ${manager(c.rejected.by)}${c.rejected.on ? ` · ${shortDate(c.rejected.on)}` : ''}`, reason: c.rejected.reason } : null,
    lock,
    countLabel: locked ? 'Final Count' : 'Your Count',
    rows,
    extras,
    bar: locked
      ? { note: { text: 'Read only', tone: 'readonly' }, actions: [] }
      : {
          note: ready ? { text: '✓ Ready to submit', tone: 'ready' }
            : done < lines.length ? { text: `${done} of ${lines.length} counted`, tone: 'missing' }
            : { text: needNote > 1 ? 'Add a note for the missing kit tools' : 'Add a note for the missing kit tool', tone: 'missing' },
          actions: [{ id: 'save', label: 'Save Draft', look: 'outline', ready: true }, { id: 'submit', label: 'Submit Count →', look: 'primary', ready }],
        },
    ready,
    assignedBy: manager(c.assigned_by),
  }
}

export const submittedDialog = (c: Pick<ToolCount, 'number' | 'assigned_by'>) =>
  ({ title: 'Count Submitted', body: `${c.number} went to ${manager(c.assigned_by)} for review. You’ll see it here as Approved or Sent Back.` })

// ---------------------------------------------------------------------------------------------------------------------
// Transfers & Sign-Offs

export type CardAction =
  | { readonly kind: 'sign'; readonly label: string; readonly ids: readonly number[] }
  | { readonly kind: 'decline'; readonly label: string; readonly ids: readonly number[] }
  | { readonly kind: 'return'; readonly label: 'Return Tool · Sign →'; readonly toolId: number }
  | { readonly kind: 'returnKit'; readonly label: 'Return Kit · Sign →'; readonly kitId: number }
  | { readonly kind: 'cancel'; readonly label: 'Cancel Transfer'; readonly id: number }

export interface HandoffCardVM {
  readonly key: string
  readonly number: string
  readonly badge: StatusLook
  readonly name: string
  readonly idLine: string
  readonly line: string
  readonly note: string | null
  readonly actions: readonly CardAction[]
}

export interface TransfersVM {
  readonly waiting: { readonly n: number; readonly sub: string }
  readonly cards: readonly HandoffCardVM[]
  readonly chargeBacks: { readonly n: number; readonly sub: string; readonly cards: readonly HandoffCardVM[] } | null
}

/** A checked-out tool, or a checked-out kit as one unit, with the Return it has if the tech signed one. */
interface ReturnUnit { readonly key: string; readonly name: string; readonly idLine: string; readonly tool: ToolItem; readonly co: Checkout; readonly kit: KitRef | null; readonly ret: Handoff | null; readonly action: CardAction }

function returnUnits(inv: ToolInventory): readonly ReturnUnit[] {
  const loose = inv.tools.filter(t => !t.kit && t.held.kind === 'checkout').map((t): ReturnUnit =>
    ({ key: `checkout-${t.id}`, name: t.name, idLine: idLine(t), tool: t, co: t.held as Checkout, kit: null, ret: returnOf(inv, t), action: { kind: 'return', label: 'Return Tool · Sign →', toolId: t.id } }))
  const kits = kitsOf(inv).filter(k => k.tools[0].held.kind === 'checkout').map(({ kit, tools }): ReturnUnit => ({
    key: `checkout-kit-${kit.id}`, name: kit.name, idLine: `${kit.number} · ${tools.map(t => t.tag || t.serial || '').join(', ')}`,
    tool: tools[0], co: tools[0].held as Checkout, kit, ret: returnOf(inv, tools[0]), action: { kind: 'returnKit', label: 'Return Kit · Sign →', kitId: kit.id },
  }))
  return [...loose, ...kits]
}

function checkoutCard(inv: ToolInventory, u: ReturnUnit, today: IsoDate): HandoffCardVM {
  const { co, ret } = u
  const number = u.kit ? u.kit.number : inv.handoffs.find(h => h.id === co.handoff_id)?.number ?? 'Check-out'
  if (ret?.status === 'Pending') {
    return {
      key: `return-${ret.id}`, number: ret.number, badge: { label: `Returned · waiting on ${confirmer(ret)}`, tone: 'muted' }, name: u.name, idLine: u.idLine,
      line: `You signed it back to ${shop(co.from)} · ${longDate(ret.sent_on)} · stays on you until they confirm`, note: null, actions: [],
    }
  }
  const bounced = ret?.status === 'Declined'
  const due = toIsoDate(co.due_on)
  const late = !!due && daysBetween(today, due) < 0
  return {
    key: u.key, number,
    badge: bounced ? { label: 'Return not received', tone: 'rejected' } : late ? { label: 'Overdue · return it', tone: 'rejected' } : { label: u.kit ? 'Kit checked out to you' : 'Checked out to you', tone: 'progress' },
    name: u.name, idLine: u.idLine,
    line: `Due back ${due ? monthDayYear(due) : ''} · return to ${shop(co.from)}`,
    note: bounced ? `${ret.decided_by || 'Shop'}: ${ret.reply || 'We do not have it'}` : co.note || null,
    actions: [u.action],
  }
}

/** The decline dialog for a card: a kit is declined whole and needs a reason, for example which tool was missing. */
export function declineDialogFor(group: readonly [Handoff, ...Handoff[]]): DeclineDialog | null {
  const [h] = group
  const copy = HANDOFF_KIND[h.kind].inbox
  if (!copy) return null
  const d = copy.decline(h, group.length)
  return h.kit && h.kind !== 'Return'
    ? { ...d, title: `Decline ${h.kit.number}`, message: 'Why are you declining? Say which tool was missing, for example. This goes to your manager.', reason: '', required: true, done: { title: 'Declined', body: 'Your manager was told. The kit did not move.' } }
    : d
}

function inboxCard(group: readonly [Handoff, ...Handoff[]]): HandoffCardVM {
  const [h] = group
  const n = group.length
  const copy = HANDOFF_KIND[h.kind].inbox
  if (!copy) throw new Error(`${h.kind} never waits on the receiver`)
  const ids = group.map(x => x.id)
  const tools = group.map(x => x.tool).filter((t): t is HandoffTool => !!t)
  const kit = h.kit
  // A kit lists its tools by tag; loose tools keep the inbox's newest-first order.
  const tags = (kit ? tools.slice().sort((a, b) => (a.tag ?? '').localeCompare(b.tag ?? '')) : tools).map(t => t.tag || t.serial || '').join(', ')
  const base = { key: `inbox-${h.id}`, line: copy.line(h), note: h.note || null, actions: [{ kind: 'decline' as const, label: copy.declineLabel, ids }, { kind: 'sign' as const, label: kit && h.kind === 'Checkout' ? 'I Have the Kit · Sign →' : copy.accept(n), ids }] }
  if (h.kind === 'Driver') {
    const v = h.vehicle
    return { ...base, number: h.number, badge: { label: copy.badge(1), tone: 'rejected' }, name: v?.name ?? 'Truck', idLine: `Truck · ${plural(v?.items.length ?? 0, 'tool', 'tools')} on it` }
  }
  if (kit) {
    const word = h.kind === 'Return' ? 'check-in' : 'check-out'
    return { ...base, number: kit.number, badge: { label: h.kind === 'Return' ? copy.badge(2) : `Kit ${word} · ${plural(n, 'tool', 'tools')}`, tone: 'rejected' }, name: kit.name, idLine: tags }
  }
  return {
    ...base,
    number: n > 1 ? `${n} tools` : h.number,
    badge: { label: copy.badge(n), tone: 'rejected' },
    name: n > 1 ? tools.map(t => t.name).join(' · ') : h.tool?.name ?? '',
    idLine: n > 1 ? tags : h.tool ? idLine(h.tool) : '',
  }
}

/** Pending handoffs to me, those sent together (same group_key and kind) folded into one card. */
function inboxGroups(inv: ToolInventory): readonly (readonly [Handoff, ...Handoff[]])[] {
  const groups: [Handoff, ...Handoff[]][] = []
  for (const h of inbox(inv)) {
    const g = h.group_key ? groups.find(x => x[0].group_key === h.group_key && x[0].kind === h.kind) : undefined
    if (g) g.push(h)
    else groups.push([h])
  }
  return groups
}

const RECENT_TRANSFERS = 3

export function transfersVM(inv: ToolInventory, today: IsoDate): TransfersVM {
  const mine = fromMe(inv)
  const outbox = inv.handoffs.filter(h => isPending(h) && mine(h) && h.kind === 'Transfer').sort(byIdDesc)
  const recent = inv.handoffs.filter(h => !isPending(h) && mine(h) && h.kind === 'Transfer' && h.decided_on).sort(byIdDesc).slice(0, RECENT_TRANSFERS)
  const groups = inboxGroups(inv)
  const waiting = groups.length
  const cards: HandoffCardVM[] = [
    ...returnUnits(inv).map(u => checkoutCard(inv, u, today)),
    ...groups.map(inboxCard),
    ...outbox.map((h): HandoffCardVM => ({
      key: `outbox-${h.id}`, number: h.number, badge: { label: `Waiting on ${h.to.label}`, tone: 'progress' }, name: subjectName(h), idLine: h.tool ? idLine(h.tool) : '',
      line: `You started this transfer · ${longDate(h.sent_on)} · stays on your inventory until ${h.to.label} accepts`, note: h.note || null,
      actions: [{ kind: 'cancel', label: 'Cancel Transfer', id: h.id }],
    })),
    ...recent.map((h): HandoffCardVM => ({
      key: `recent-${h.id}`, number: h.number,
      badge: h.status === 'Accepted' ? { label: `Accepted by ${h.to.label}`, tone: 'approved' } : { label: h.status === 'Declined' ? `Declined by ${h.to.label}` : h.status, tone: 'muted' },
      name: subjectName(h), idLine: h.tool ? idLine(h.tool) : '',
      line: `${h.status === 'Accepted' ? 'Off your inventory' : 'Still on your inventory'} · ${longDate(h.decided_on)}`,
      note: h.reply ? `${h.to.label}: ${h.reply}` : null, actions: [],
    })),
  ]
  const cbAll = inv.handoffs.filter(h => h.kind === 'ChargeBack' && toMe(inv)(h) && h.status !== 'Cancelled')
    .sort((a, b) => Number(!isPending(a)) - Number(!isPending(b)) || b.id - a.id)
  const cbPending = cbAll.filter(isPending).length
  return {
    waiting: { n: waiting, sub: waiting ? 'Check that you have the tool, then sign for it. Your manager sees it right away.' : 'Nothing to sign for right now.' },
    cards,
    chargeBacks: cbAll.length === 0 ? null : {
      n: cbPending,
      sub: cbPending ? 'Review each charge-back, then sign to authorize the deduction or dispute it.' : 'Your charge-back history.',
      cards: cbAll.map((h): HandoffCardVM => isPending(h) ? inboxCard([h]) : {
        key: `cb-${h.id}`, number: h.number,
        badge: h.status === 'Accepted' ? { label: 'Signed · deducted next paycheck', tone: 'approved' } : { label: 'Disputed', tone: 'progress' },
        name: subjectName(h), idLine: h.tool ? idLine(h.tool) : '',
        line: `${h.charge?.loss_kind ?? 'Lost'} tool · ${money(h.charge?.amount)} · ${h.status === 'Accepted' ? 'signed' : 'disputed'} ${longDate(h.decided_on)}`,
        note: h.reply ? `You: ${h.reply}` : h.note || null, actions: [],
      }),
    },
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// Sign dialog

/** What the tech has done in the dialog so far. */
export interface SignForm {
  readonly typed: string
  readonly agreed: boolean
  /** On a return: who they hand it to, from the inventory's receivers. */
  readonly receiverId: number | null
  /** On a kit return: the tools they could not find, and where those are. */
  readonly missing: readonly number[]
  readonly note: string
}

export const EMPTY_SIGN_FORM: SignForm = { typed: '', agreed: false, receiverId: null, missing: [], note: '' }

export interface SignVM {
  readonly number: string
  readonly title: string
  readonly tag: string
  readonly name: string
  readonly serialLine: string
  /** Every tool covered, numbered, when there are several, a kit or a truck. */
  readonly list: { readonly title: string; readonly rows: readonly { readonly n: number; readonly id: number; readonly name: string; readonly idLine: string }[] } | null
  /** A kit return counts every tool: each row is in the kit or missing. */
  readonly checklist: readonly { readonly id: number; readonly name: string; readonly idLine: string; readonly inKit: boolean }[] | null
  readonly missingNote: { readonly hint: string; readonly required: boolean } | null
  /** On a return: who to hand it to; `required` once the shop has named receivers. */
  readonly receivers: { readonly options: readonly { readonly id: number; readonly label: string }[]; readonly required: boolean; readonly chosen: boolean } | null
  readonly agreement: string
  readonly button: string
  readonly message: { readonly text: string; readonly tone: 'muted' | 'rejected' | 'approved' }
  readonly nameOk: boolean
  readonly canSign: boolean
}

/** Trimmed, case-insensitive, inner whitespace collapsed: "  bobby   swink " signs for "Bobby Swink". */
export function signatureMatches(typed: string, name: string): boolean {
  const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase()
  return norm(typed).length > 0 && norm(typed) === norm(name)
}

const receiverLabel = (r: Receiver) => `${r.name} · ${r.role === 'manager' ? 'your manager' : 'shop check-in'}`
const signIdLine = (t: SignTool) => `${t.tag || 'No ID'} · S/N ${t.serial ?? ''}${t.category ? ` · ${t.category}` : ''}`

export function signVM(subject: SignSubject, form: SignForm, me: string, today: IsoDate, receivers: readonly Receiver[] = []): SignVM {
  const returning = subject.kind === 'return' || subject.kind === 'returnKit'
  const receiver = returning ? receivers.find(r => r.id === form.receiverId) ?? null : null
  const f = signFacts(subject, receiver?.name ?? null)
  const def = SIGN_COPY[f.kind]
  const nameOk = signatureMatches(typed(form), me)
  const listTitle = def.listTitle(f)
  const missing = subject.kind === 'returnKit' ? form.missing.filter(id => subject.tools.some(t => t.id === id)) : []
  const needsNote = missing.length > 0
  const noteOk = !needsNote || form.note.trim().length > 0
  const receiverOk = !returning || receivers.length === 0 || !!receiver
  return {
    number: f.number, title: def.signTitle(f), tag: f.tag, name: f.name,
    serialLine: `${f.kind === 'Driver' || f.kit || f.tools.length > 1 ? f.serial : `S/N ${f.serial}`} · ${def.fromLine(f)}`,
    list: listTitle && subject.kind !== 'returnKit' ? { title: listTitle, rows: f.tools.map((t, i) => ({ n: i + 1, id: t.id, name: t.name, idLine: signIdLine(t) })) } : null,
    checklist: subject.kind === 'returnKit' ? subject.tools.map(t => ({ id: t.id, name: t.name, idLine: signIdLine(t), inKit: !missing.includes(t.id) })) : null,
    // The note box only appears once a tool is marked missing; a whole kit has nothing to say.
    missingNote: subject.kind === 'returnKit' && needsNote
      ? { hint: `${plural(missing.length, 'tool is', 'tools are')} missing. The kit still goes back whole. Say where ${missing.length > 1 ? 'they are' : 'it is'}. Required.`, required: true }
      : null,
    receivers: returning && receivers.length ? { options: receivers.map(r => ({ id: r.id, label: receiverLabel(r) })), required: true, chosen: !!receiver } : null,
    agreement: def.agreement(f), button: def.signButton,
    message: nameOk ? { text: `Signed electronically as ${me} · ${monthDayYear(today)}`, tone: 'approved' }
      : typed(form).trim() ? { text: `Type your name exactly as it shows on your account: ${me}`, tone: 'rejected' }
      : { text: 'Typing your name is your electronic signature.', tone: 'muted' },
    nameOk, canSign: nameOk && form.agreed && noteOk && receiverOk,
  }
}
const typed = (form: SignForm) => form.typed

/** The toast after signing; `receiver` is who a return was handed to. */
export const signedToast = (subject: SignSubject, receiver: string | null = null) => SIGN_COPY[signFacts(subject, receiver).kind].done(signFacts(subject, receiver))

/** The sign subject for a card action, or null once the rows it named are gone or no longer pending. */
export function signSubjectFor(inv: ToolInventory, action: Extract<CardAction, { kind: 'sign' | 'return' | 'returnKit' }>): SignSubject | null {
  if (action.kind === 'return') {
    const tool = inv.tools.find(t => t.id === action.toolId)
    if (!tool || tool.held.kind !== 'checkout') return null
    const number = inv.handoffs.find(h => h.id === (tool.held as Checkout).handoff_id)?.number ?? 'Check-out'
    return { kind: 'return', tool, checkout: tool.held, number }
  }
  if (action.kind === 'returnKit') {
    const k = kitsOf(inv).find(x => x.kit.id === action.kitId)
    if (!k || k.tools[0].held.kind !== 'checkout') return null
    return { kind: 'returnKit', kit: k.kit, tools: k.tools, checkout: k.tools[0].held, number: k.kit.number }
  }
  const named = action.ids.map(id => inv.handoffs.find(h => h.id === id)).filter((h): h is Handoff => !!h && isPending(h))
  // A kit signs whole: a deep link naming one of its handoffs opens the whole kit.
  const handoffs: Handoff[] = []
  for (const h of named) {
    const group = h.kit && h.group_key ? inv.handoffs.filter(x => isPending(x) && x.kit?.id === h.kit?.id && x.group_key === h.group_key && x.kind === h.kind) : [h]
    for (const x of group) if (!handoffs.includes(x)) handoffs.push(x)
  }
  return handoffs.length ? { kind: 'accept', handoffs: handoffs as [Handoff, ...Handoff[]] } : null
}

// ---------------------------------------------------------------------------------------------------------------------
// Lost & Broken

export interface ReportCardVM {
  readonly key: number
  readonly number: string
  readonly status: StatusLook
  readonly name: string
  readonly idLine: string
  readonly what: string
  readonly note: string | null
  readonly foot: string
}

export interface ReportsVM { readonly n: number; readonly cards: readonly ReportCardVM[] }

export function reportsVM(inv: ToolInventory): ReportsVM {
  const cards = inv.reports.slice().sort(byIdDesc).map((r): ReportCardVM => ({
    key: r.id, number: r.number, status: REPORT_STATUS[r.status], name: r.tool.name, idLine: idLine(r.tool),
    what: `${r.type !== 'Broken' ? `${r.type} · ` : ''}${r.where ? `${r.where} · ` : ''}${r.what}`,
    note: r.manager_note ? `Manager: ${r.manager_note}` : null,
    foot: `Sent ${longDate(r.sent_at)} · ${REPORT_STATUS[r.status].line(r)}`,
  }))
  return { n: cards.length, cards }
}

// ---------------------------------------------------------------------------------------------------------------------
// Transfer and report forms

export interface PickableTool { readonly id: number; readonly tag: string; readonly name: string; readonly sub: string; readonly busy: string | null }

export interface TransferFormVM {
  readonly tools: readonly PickableTool[]
  readonly technicians: ToolInventory['technicians']
  /** What Send Transfer still needs, in the form's order. */
  readonly missing: readonly ('tool' | 'technician')[]
  readonly note: string
}

const TRANSFER_NOTE = 'The tool stays on your inventory until they accept it in their app. If they decline, it stays with you. Kits can’t be transferred: they only go out on a temporary check-out from the shop.'

const pickable = (t: ToolItem, busy: string | null): PickableTool => ({ id: t.id, tag: t.tag || '—', name: t.name, sub: `S/N ${t.serial ?? ''} · ${t.kit ? `in ${t.kit.number} · ` : ''}${heldLabel(t)}`, busy })

/** Kits never transfer tech to tech, so their tools are not offered. */
export function transferFormVM(inv: ToolInventory, toolId: number | null, toUserId: number | null): TransferFormVM {
  const pending = new Set(inv.handoffs.filter(h => isPending(h) && fromMe(inv)(h) && h.kind === 'Transfer').map(h => h.tool?.id))
  const tools = inv.tools.filter(t => t.held.kind !== 'checkout' && !t.kit).map(t => pickable(t, pending.has(t.id) ? 'Transfer pending' : null))
  const missing: TransferFormVM['missing'] = [
    ...(tools.some(t => t.id === toolId && !t.busy) ? [] : ['tool' as const]),
    ...(inv.technicians.some(t => t.id === toUserId) ? [] : ['technician' as const]),
  ]
  return { tools, technicians: inv.technicians, missing, note: TRANSFER_NOTE }
}

export const transferSentDialog = (tool: Pick<ToolItem, 'name'>, tech: { readonly name: string }) =>
  ({ title: 'Transfer Sent', body: `${tool.name} goes to ${tech.name} once they sign for it. It stays on your inventory until then.` })

export interface ReportDraft { readonly toolId: number | null; readonly type: ReportType; readonly where: string; readonly what: string; readonly usable: boolean }

export interface ReportFormVM {
  readonly tools: readonly PickableTool[]
  readonly types: readonly ReportType[]
  readonly whereLabel: string
  readonly wherePlaceholder: string
  readonly whatPlaceholder: string
  readonly foot: string
  /** What Send Report still needs, in the form's order. */
  readonly missing: readonly ('tool' | 'where' | 'what')[]
}

export function reportFormVM(inv: ToolInventory, d: ReportDraft): ReportFormVM {
  const reported = new Set(inv.reports.filter(r => r.status === 'Pending').map(r => r.tool.id))
  const tools = inv.tools.map(t => pickable(t, reported.has(t.id) ? 'Already reported' : null))
  const def = REPORT_TYPE[d.type]
  const missing: ReportFormVM['missing'] = [
    ...(tools.some(t => t.id === d.toolId && !t.busy) ? [] : ['tool' as const]),
    ...(d.where.trim() ? [] : ['where' as const]),
    ...(d.what.trim() ? [] : ['what' as const]),
  ]
  return { tools, types: REPORT_TYPES, whereLabel: def.whereLabel, wherePlaceholder: WHERE_PLACEHOLDER, whatPlaceholder: def.whatPlaceholder, foot: def.foot, missing }
}

/** A report on a kit tool marks the whole kit Needs Replacement until the shop swaps the tool. */
export const reportSentDialog = (type: ReportType, tool: Pick<ToolItem, 'name' | 'tag' | 'kit'>) => ({
  title: 'Report Sent',
  body: `${type} report for ${tool.name}${tool.tag ? ` (${tool.tag})` : ''} went to your manager.${tool.kit ? ` ${tool.kit.number} is now marked Needs Replacement until the shop swaps the tool.` : ''} You’ll see their decision here under Lost & Broken.`,
})

// ---------------------------------------------------------------------------------------------------------------------
// Tab badges and Schedule callouts

export type TabBadges = { readonly [K in ToolsTab]: number }

/** Checked-out tools and kits still on the tech's hands: no Return signed, or the receiver declined the one that was. */
function returnsDue(inv: ToolInventory, today: IsoDate) {
  return returnUnits(inv)
    .map(u => { const due = toIsoDate(u.co.due_on); return { ...u, bounced: u.ret?.status === 'Declined', waiting: u.ret?.status === 'Pending', days: due ? daysBetween(today, due) : Number.POSITIVE_INFINITY, due } })
    .filter(r => !r.waiting)
    .sort((a, b) => a.days - b.days)
}

export function tabBadges(inv: ToolInventory, today: IsoDate): TabBadges {
  return {
    mine: 0,
    counts: openCounts(inv).length,
    transfers: inboxGroups(inv).length + returnsDue(inv, today).filter(r => r.days < 0).length + pendingChargeBacks(inv).length,
    reports: inv.reports.filter(r => r.status === 'Pending').length,
  }
}

const go = (target: NavTarget): CalloutCard['action'] => ({ kind: 'go', target })

function countCallouts(inv: ToolInventory, today: IsoDate): CalloutCard[] {
  const open = openCounts(inv).map(c => { const due = toIsoDate(c.due_on); return { c, due, left: due ? daysBetween(today, due) : null } })
  if (open.length > 1) {
    const sentBack = open.filter(o => o.c.status === 'Rejected').length
    const dueSoon = open.filter(o => o.c.status !== 'Rejected' && o.left != null && o.left <= 1).length
    const next = open.map(o => o.due).filter((d): d is IsoDate => !!d).sort()[0]
    return [{
      id: 'tools-counts', tone: sentBack || dueSoon ? 'red' : 'steel', title: 'Tool Counts',
      sub: `${open.length} counts to do${next ? ` · next due ${monthDay(next)}` : ''}`, cta: 'Count', action: go(links.tools('counts')),
    }]
  }
  return open.map(({ c, due, left }) => {
    const rejected = c.status === 'Rejected'
    const late = left != null && left <= 1
    return {
      id: `tools-count-${c.id}`, tone: rejected || late ? 'red' : c.status === 'In Progress' ? 'amber' : 'steel', title: 'Tool Count',
      sub: rejected ? 'Fix and send it back' : `Due ${due ? monthDay(due) : ''}`,
      cta: rejected ? 'Fix' : c.status === 'In Progress' ? 'Continue' : 'Count', action: go(links.toolCount(c.id)),
    }
  })
}

function calibrationCallout(inv: ToolInventory, today: IsoDate): CalloutCard | null {
  const rows = inv.tools.map(t => {
    const lines = ([['Calibration', t.next_cal_due], ['Bump test', t.next_bump_due]] as const)
      .map(([what, raw]) => { const on = toIsoDate(raw); return on ? { what, on, days: daysBetween(today, on) } : null })
      .filter((x): x is { what: 'Calibration' | 'Bump test'; on: IsoDate; days: number } => !!x && x.days <= SERVICE_WINDOW_DAYS)
      .sort((a, b) => a.days - b.days)
    return {
      name: t.name, tag: t.tag ?? '', overdue: lines.some(l => l.days < 0),
      lines: lines.map(l => ({ text: l.days < 0 ? `${l.what} overdue` : l.days === 0 ? `${l.what} due today` : `${l.what} due ${monthDay(l.on)}`, urgent: l.days <= 0 })),
    }
  }).filter(r => r.lines.length > 0)
  if (!rows.length) return null
  const over = rows.filter(r => r.overdue).length
  return {
    id: 'tools-cal', tone: over ? 'red' : 'amber', title: 'Calibration', sub: `${rows.length} ${rows.length === 1 ? 'tool needs' : 'tools need'} the shop`, cta: 'See Tools',
    action: { kind: 'expand', rows: rows.map(({ name, tag, lines }) => ({ name, tag, lines })), foot: 'Get these to the shop before the due date.' },
  }
}

/** The Schedule's tool cards: charge-backs, sign-offs, check-outs, returns, counts and calibration. */
export function toolCallouts(ctx: CalloutContext): readonly CalloutCard[] {
  const inv = ctx.tools
  if (!inv) return []
  const today = ctx.today
  const cards: CalloutCard[] = []
  const cbs = pendingChargeBacks(inv)
  if (cbs.length) {
    const [cb] = cbs
    cards.push({
      id: 'tools-cb', tone: 'red', title: 'Sign charge-back',
      sub: cbs.length === 1 ? `${cb.number} · ${money(cb.charge?.amount)} for the ${(cb.charge?.loss_kind ?? 'Lost').toLowerCase()} ${subjectName(cb)}` : `${cbs.length} charge-backs need your signature`,
      cta: 'Sign', action: go(cbs.length === 1 ? links.tools('transfers', { sign: String(cb.id) }) : links.tools('transfers')),
    })
  }
  const signFor = inbox(inv).filter(h => h.kind === 'Assignment' || h.kind === 'Transfer').length
  if (signFor) {
    cards.push({
      id: 'tools-sign', tone: 'red', title: signFor === 1 ? 'Tool to sign for' : `${signFor} tools to sign for`,
      sub: signFor === 1 ? 'Confirm you have it so it moves to your inventory' : 'Confirm you have them so they move to your inventory',
      cta: 'Sign', action: go(links.tools('transfers')),
    })
  }
  const checkouts = inbox(inv).filter(h => h.kind === 'Checkout').length
  if (checkouts) cards.push({ id: 'tools-checkout', tone: 'red', title: 'Tool check-out', sub: `${plural(checkouts, 'tool', 'tools')} waiting for your signature`, cta: 'Sign', action: go(links.tools('transfers')) })
  const trucks = inbox(inv).filter(h => h.kind === 'Driver')
  if (trucks.length) {
    const [t] = trucks
    cards.push({
      id: 'tools-truck', tone: 'red', title: trucks.length === 1 ? 'Truck to sign for' : `${trucks.length} trucks to sign for`,
      sub: trucks.length === 1 ? `${manager(t.by)} is making you the driver of ${subjectName(t)}` : 'Accept each truck and the tools on it',
      cta: 'Sign', action: go(trucks.length === 1 ? links.tools('transfers', { sign: String(t.id) }) : links.tools('transfers')),
    })
  }
  const checkIns = inboxGroups(inv).filter(g => g[0].kind === 'Return')
  if (checkIns.length) {
    const [[c]] = checkIns
    cards.push({
      id: 'tools-checkin', tone: 'amber', title: checkIns.length === 1 ? 'Check-in to confirm' : `${checkIns.length} check-ins to confirm`,
      sub: checkIns.length === 1 ? `${c.from.label} returned ${c.kit ? c.kit.number : subjectName(c)} to ${shop(c.return_to)}` : 'Sign for what came back, or say it was not received',
      cta: 'Confirm', action: go(links.tools('transfers')),
    })
  }
  const returns = returnsDue(inv, today)
  if (returns.length) {
    const [first] = returns
    const late = returns.filter(r => r.days < 0).length
    const bounced = returns.some(r => r.bounced)
    const what = (u: ReturnUnit) => (u.kit ? `${u.kit.number} ${u.kit.name}` : `${u.tool.tag ? `${u.tool.tag} ` : ''}${u.tool.name}`)
    cards.push({
      id: 'tools-return', tone: late || bounced ? 'red' : first.days <= 1 ? 'amber' : 'steel',
      title: returns.length === 1 ? (first.kit ? 'Return kit' : 'Return tool') : `Return ${returns.length} items`,
      sub: returns.length === 1
        ? `${what(first)} · to ${shop(first.co.from)}`
        : `Next: ${first.name} · due ${first.due ? monthDay(first.due) : ''}`,
      cta: 'Return', action: go(links.tools('transfers')),
    })
  }
  cards.push(...countCallouts(inv, today))
  const cal = calibrationCallout(inv, today)
  if (cal) cards.push(cal)
  return cards
}
